/**
 * MoneySelectorModal.ts - 화폐 단위(코퍼/실버/골드/플래티넘) 수량 선택 및 거래 커서 모드
 */

import { EntityState } from '../../core/Types';
import { Currency, COPPER_PER_SILVER, COPPER_PER_GOLD, COPPER_PER_PLATINUM } from '../../core/Currency';
import { tradeModalInstance } from './TradeModal';
import { EventBus } from '../../core/EventBus';

export type CurrencyTier = 'copper' | 'silver' | 'gold' | 'platinum';

export class MoneySelectorModal {
  private el: HTMLElement | null = null;
  private currentTier: CurrencyTier = 'gold';
  private selectedAmount: number = 1;
  private maxAmount: number = 1;
  private player: EntityState | null = null;

  // Active money cursor payload
  private moneyInHandCopper: number = 0;
  private cursorIndicatorEl: HTMLElement | null = null;

  constructor() {
    this.createDom();
    this.createCursorIndicator();
    this.bindGlobalPointer();
  }

  private createDom(): void {
    if (document.getElementById('money-selector-modal')) return;

    const modal = document.createElement('div');
    modal.id = 'money-selector-modal';
    modal.style.cssText = `
      position: fixed;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 290px;
      background: linear-gradient(180deg, #2b2218, #18120b);
      border: 2px solid #7a5c2e;
      border-radius: 6px;
      box-shadow: 0 0 0 1px #000, inset 0 0 0 1px #c9a45a44, 0 10px 25px rgba(0,0,0,0.8);
      color: #e8dcc0;
      font-family: sans-serif;
      z-index: 10001;
      display: none;
      flex-direction: column;
      user-select: none;
      padding: 12px 14px;
    `;

    document.body.appendChild(modal);
    this.el = modal;
  }

  private createCursorIndicator(): void {
    const ind = document.createElement('div');
    ind.id = 'money-cursor-indicator';
    ind.style.cssText = `
      position: fixed;
      pointer-events: none;
      z-index: 100000;
      display: none;
      transform: translate(12px, 12px);
      background: rgba(15, 23, 42, 0.95);
      border: 1.5px solid #fde047;
      box-shadow: 0 4px 12px rgba(0,0,0,0.6);
      border-radius: 6px;
      padding: 4px 8px;
      font-size: 11px;
      font-weight: bold;
      color: #fde047;
      white-space: nowrap;
      align-items: center;
      gap: 5px;
    `;
    document.body.appendChild(ind);
    this.cursorIndicatorEl = ind;
  }

  private bindGlobalPointer(): void {
    window.addEventListener('mousemove', (e) => {
      if (this.cursorIndicatorEl && this.cursorIndicatorEl.style.display !== 'none') {
        this.cursorIndicatorEl.style.left = `${e.clientX}px`;
        this.cursorIndicatorEl.style.top = `${e.clientY}px`;
      }
    });

    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.hasMoneyInHand()) {
        this.cancelMoneyInHand();
      }
    });
  }

  public open(player: EntityState, tier: CurrencyTier): void {
    this.player = player;
    this.currentTier = tier;

    const totalCopper = player.gold ?? 0;
    const b = Currency.breakdown(totalCopper);

    // Calculate maximum available for this specific tier
    if (tier === 'copper') {
      this.maxAmount = b.copper;
    } else if (tier === 'silver') {
      this.maxAmount = b.silver;
    } else if (tier === 'gold') {
      this.maxAmount = b.gold;
    } else {
      this.maxAmount = b.platinum;
    }

    if (this.maxAmount <= 0) {
      alert(`보유한 ${this.getTierLabel(tier)}가 없습니다.`);
      return;
    }

    this.selectedAmount = Math.min(1, this.maxAmount);
    this.render();
  }

  public close(): void {
    if (this.el) {
      this.el.style.display = 'none';
      this.el.innerHTML = '';
    }
  }

  private getTierLabel(tier: CurrencyTier): string {
    switch (tier) {
      case 'copper': return '코퍼 (C)';
      case 'silver': return '실버 (S)';
      case 'gold': return '골드 (G)';
      case 'platinum': return '플래티넘 (P)';
    }
  }

  private getTierIcon(tier: CurrencyTier): string {
    switch (tier) {
      case 'copper': return '🪙';
      case 'silver': return '🪙';
      case 'gold': return '🪙';
      case 'platinum': return '🪙';
    }
  }

  private getTierColor(tier: CurrencyTier): string {
    switch (tier) {
      case 'copper': return '#f97316';
      case 'silver': return '#cbd5e1';
      case 'gold': return '#fde047';
      case 'platinum': return '#a78bfa';
    }
  }

  private render(): void {
    if (!this.el) return;
    this.el.style.display = 'flex';

    const tierName = this.getTierLabel(this.currentTier);
    const color = this.getTierColor(this.currentTier);

    this.el.innerHTML = `
      <div style="display:flex; justify-content:space-between; align-items:center; border-bottom:1px solid #5a431f; padding-bottom:6px; margin-bottom:10px;">
        <span style="font-size:12px; font-weight:bold; color:${color};">
          ${this.getTierIcon(this.currentTier)} ${tierName} 수량 지정
        </span>
        <button id="btn-money-modal-close" style="background:none; border:none; color:#f3d98b; font-size:13px; cursor:pointer;">✕</button>
      </div>

      <div style="font-size:11px; color:#cbd5e1; margin-bottom:8px;">
        거래할 금액을 정해주세요 (1 ~ ${this.maxAmount.toLocaleString()}):
      </div>

      <!-- Arrow Stepper Controller -->
      <div style="display:flex; align-items:center; justify-content:center; gap:8px; background:#0c0905; border:1px solid #5a431f; border-radius:4px; padding:8px; margin-bottom:12px;">
        <button id="btn-step-min" style="background:#1e293b; border:1px solid #475569; color:#cbd5e1; padding:3px 7px; border-radius:3px; font-size:10px; cursor:pointer;">최소</button>
        <button id="btn-step-dec-10" style="background:#334155; border:1px solid #64748b; color:#fff; padding:3px 8px; border-radius:3px; font-size:11px; cursor:pointer; font-weight:bold;">-10</button>
        <button id="btn-step-dec" style="background:#475569; border:1px solid #94a3b8; color:#fff; width:26px; height:26px; border-radius:3px; font-size:13px; cursor:pointer; font-weight:bold;">◀</button>
        
        <input id="input-money-amount" type="number" value="${this.selectedAmount}" min="1" max="${this.maxAmount}" style="width:75px; text-align:center; background:#18120b; border:1.5px solid #fde047; color:#fde047; font-weight:bold; font-size:14px; border-radius:4px; padding:3px 0;" />
        
        <button id="btn-step-inc" style="background:#475569; border:1px solid #94a3b8; color:#fff; width:26px; height:26px; border-radius:3px; font-size:13px; cursor:pointer; font-weight:bold;">▶</button>
        <button id="btn-step-inc-10" style="background:#334155; border:1px solid #64748b; color:#fff; padding:3px 8px; border-radius:3px; font-size:11px; cursor:pointer; font-weight:bold;">+10</button>
        <button id="btn-step-max" style="background:#1e293b; border:1px solid #475569; color:#cbd5e1; padding:3px 7px; border-radius:3px; font-size:10px; cursor:pointer;">최대</button>
      </div>

      <!-- Action Buttons -->
      <div style="display:flex; justify-content:flex-end; gap:6px;">
        <button id="btn-money-cancel" style="background:#450a0a; border:1px solid #ef4444; color:#fff; padding:6px 12px; border-radius:4px; font-size:11px; font-weight:bold; cursor:pointer;">
          취소
        </button>
        <button id="btn-money-confirm" style="background:#0284c7; border:1px solid #38bdf8; color:#fff; padding:6px 16px; border-radius:4px; font-size:11px; font-weight:bold; cursor:pointer;">
          확인
        </button>
      </div>
    `;

    this.bindEvents();
  }

  private bindEvents(): void {
    if (!this.el) return;

    const input = this.el.querySelector('#input-money-amount') as HTMLInputElement;

    const updateVal = (val: number) => {
      this.selectedAmount = Math.max(1, Math.min(this.maxAmount, Math.floor(val || 1)));
      if (input) input.value = String(this.selectedAmount);
    };

    input?.addEventListener('change', () => updateVal(parseInt(input.value, 10)));
    input?.addEventListener('input', () => updateVal(parseInt(input.value, 10)));

    this.el.querySelector('#btn-step-min')?.addEventListener('click', () => updateVal(1));
    this.el.querySelector('#btn-step-max')?.addEventListener('click', () => updateVal(this.maxAmount));
    this.el.querySelector('#btn-step-dec')?.addEventListener('click', () => updateVal(this.selectedAmount - 1));
    this.el.querySelector('#btn-step-inc')?.addEventListener('click', () => updateVal(this.selectedAmount + 1));
    this.el.querySelector('#btn-step-dec-10')?.addEventListener('click', () => updateVal(this.selectedAmount - 10));
    this.el.querySelector('#btn-step-inc-10')?.addEventListener('click', () => updateVal(this.selectedAmount + 10));

    this.el.querySelector('#btn-money-modal-close')?.addEventListener('click', () => this.close());
    this.el.querySelector('#btn-money-cancel')?.addEventListener('click', () => this.close());

    this.el.querySelector('#btn-money-confirm')?.addEventListener('click', () => {
      let copperValue = 0;
      if (this.currentTier === 'copper') copperValue = this.selectedAmount;
      else if (this.currentTier === 'silver') copperValue = this.selectedAmount * COPPER_PER_SILVER;
      else if (this.currentTier === 'gold') copperValue = this.selectedAmount * COPPER_PER_GOLD;
      else if (this.currentTier === 'platinum') copperValue = this.selectedAmount * COPPER_PER_PLATINUM;

      this.pickUpMoneyCursor(copperValue);
      this.close();
    });
  }

  /** Pick up money icon onto the cursor */
  private pickUpMoneyCursor(copper: number): void {
    this.moneyInHandCopper = copper;
    if (this.cursorIndicatorEl) {
      this.cursorIndicatorEl.style.display = 'flex';
      this.cursorIndicatorEl.innerHTML = `
        <span style="font-size:16px;">🪙</span>
        <span>${Currency.format(copper)} (상대방 클릭 시 거래)</span>
      `;
    }

    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: performance.now(),
      text: `🪙 [돈 집기] ${Currency.format(copper)}를 들었습니다. 거래할 상대방(NPC 또는 플레이어)을 클릭하세요! (취소: ESC)`,
      category: 'system',
    });
  }

  public hasMoneyInHand(): boolean {
    return this.moneyInHandCopper > 0;
  }

  public getMoneyInHand(): number {
    return this.moneyInHandCopper;
  }

  public cancelMoneyInHand(): void {
    this.moneyInHandCopper = 0;
    if (this.cursorIndicatorEl) {
      this.cursorIndicatorEl.style.display = 'none';
      this.cursorIndicatorEl.innerHTML = '';
    }
  }

  /** Try to trade with target clicked */
  public handleTargetClick(target: EntityState, player: EntityState): boolean {
    if (!this.hasMoneyInHand()) return false;

    const offerCopper = this.moneyInHandCopper;
    this.cancelMoneyInHand();

    // Open DAoC-style Trade Window with target!
    tradeModalInstance.open(player, target, offerCopper);
    return true;
  }
}

export const moneySelectorModalInstance = new MoneySelectorModal();
